import { Controller } from '@hotwired/stimulus';
import { post, destroy } from '@rails/request.js';

/**
 * Push subscription - registers the service worker and the browser's Web Push subscription.
 * Values:  auto (Boolean) - when true (app layout) it only re-syncs an existing, granted subscription
 * Targets: enable / disable (buttons) | status (message) | messages (template holding the translated texts)
 */
export default class extends Controller {
  static targets = ['enable', 'disable', 'status', 'messages'];
  static values = { auto: { type: Boolean, default: false } };

  async connect () {
    if (!this.#supported) {
      this.#render(null, 'unsupported');
      return;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/service-worker', { scope: '/' });
      const subscription = await this.registration.pushManager.getSubscription();

      if (this.autoValue) {
        if (subscription && Notification.permission === 'granted') await this.#save(subscription);
        return;
      }

      this.#render(subscription);
    } catch (error) {
      console.warn('[push] service worker unavailable', error);
      this.#render(null, 'unsupported');
    }
  }

  async enable () {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      this.#render(null);
      return;
    }

    const subscription = await this.registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.#applicationServerKey()
    });
    await this.#save(subscription);
    this.#render(subscription);
  }

  async disable () {
    const subscription = await this.registration.pushManager.getSubscription();
    if (subscription) {
      await destroy(`/push_subscriptions?endpoint=${encodeURIComponent(subscription.endpoint)}`, { responseKind: 'json' });
      await subscription.unsubscribe();
    }
    this.#render(null);
  }

  get #supported () {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  async #save (subscription) {
    await post('/push_subscriptions', {
      body: JSON.stringify({ subscription: subscription.toJSON() }),
      contentType: 'application/json',
      responseKind: 'json'
    });
  }

  #render (subscription, forcedState = null) {
    if (this.autoValue) return;

    const state = forcedState || (subscription ? 'enabled' : (Notification.permission === 'denied' ? 'denied' : 'off'));
    const message = this.hasMessagesTarget ? this.messagesTarget.dataset[state] : null;

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message || '';
      this.statusTarget.hidden = !message;
    }
    if (this.hasEnableTarget) this.enableTarget.hidden = state !== 'off';
    if (this.hasDisableTarget) this.disableTarget.hidden = state !== 'enabled';
  }

  #applicationServerKey () {
    const key = document.querySelector('meta[name="vapid-public-key"]')?.content || '';
    const padding = '='.repeat((4 - (key.length % 4)) % 4);
    const base64 = (key + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
  }
}
